"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.TICKET_MESSAGE_ERROR_CODES = exports.postTicketMessageSchema = exports.TICKET_MESSAGE_TEXT_MAX = exports.TICKET_RECIPIENT_SOURCE_LABELS = exports.TICKET_RECIPIENT_SOURCES = exports.INTERNAL_NOTE_MARK = exports.TICKET_MESSAGE_KIND_LABELS = exports.TICKET_THREAD_MESSAGE_KINDS = exports.TICKET_MESSAGE_KINDS = void 0;
exports.ticketMessageText = ticketMessageText;
exports.replyStatePath = replyStatePath;
exports.canReply = canReply;
exports.linkifyText = linkifyText;
const zod_1 = require("zod");
const inbound_mail_js_1 = require("./inbound-mail.js");
/**
 * Scrittura dal ticket (PRD-004 US-4, story #5): risposta a chi ha segnalato e nota interna.
 * Contratti fra l'API della console (`POST /api/v1/tickets/:code/messages`) e la web app: chiavi in
 * inglese come le altre API degli operatori, valori di dominio in italiano.
 */
/**
 * Tipi di messaggio del thread scritti dagli operatori. La risposta parte come email a chi ha
 * segnalato; la nota interna non esce mai dalla console. Le repliche in ingresso (#42) sono tipi
 * a parte (`TICKET_INBOUND_MESSAGE_KINDS`): il composer non li scrive.
 */
exports.TICKET_MESSAGE_KINDS = ['risposta', 'nota'];
/** Tutti i tipi di messaggio registrati nel thread (enum del database): composer e posta in ingresso (#42). */
exports.TICKET_THREAD_MESSAGE_KINDS = [...exports.TICKET_MESSAGE_KINDS, ...inbound_mail_js_1.TICKET_INBOUND_MESSAGE_KINDS];
exports.TICKET_MESSAGE_KIND_LABELS = {
    risposta: 'Risposta',
    nota: 'Nota interna',
};
/** Marcatura della nota interna nel thread e nel composer (PRD-004 US-4). */
exports.INTERNAL_NOTE_MARK = "Non visibile all'utente";
/**
 * Fonte dell'indirizzo a cui parte la risposta: `account` = email dell'account sul canale
 * autenticato (verificata); `dichiarato` = email scritta da chi segnala sul pre-accesso, non
 * verificata. Nessun valore `caregiver`: il canale paziente non esiste (DEC-005).
 */
exports.TICKET_RECIPIENT_SOURCES = ['account', 'dichiarato'];
exports.TICKET_RECIPIENT_SOURCE_LABELS = {
    account: "dall'account, verificato",
    dichiarato: 'dichiarato, non verificato',
};
/** Lunghezza massima del testo di un messaggio, come quella della segnalazione. */
exports.TICKET_MESSAGE_TEXT_MAX = 10_000;
/**
 * Il testo del composer (DEC-008): testo semplice, spazi ai bordi tolti, da 1 a
 * `TICKET_MESSAGE_TEXT_MAX` caratteri, senza NUL (troncherebbe il testo). Lo usano il messaggio e la
 * bozza di risposta (#158), che il composer carica così com'è.
 */
function ticketMessageText() {
    return zod_1.z
        .string({ error: 'Testo: campo obbligatorio' })
        .trim()
        .min(1, { error: 'Scrivi il testo del messaggio', abort: true })
        .max(exports.TICKET_MESSAGE_TEXT_MAX, { error: `Testo troppo lungo (massimo ${exports.TICKET_MESSAGE_TEXT_MAX} caratteri)` })
        .refine((value) => !value.includes('\0'), { error: 'Il testo non può contenere caratteri di controllo' });
}
/**
 * Corpo di `POST /tickets/:code/messages`. Testo semplice (DEC-008): a capo conservati, spazi ai
 * bordi tolti, nessun HTML interpretato; il NUL è rifiutato perché troncherebbe il testo.
 */
exports.postTicketMessageSchema = zod_1.z
    .strictObject({
    kind: zod_1.z.enum(exports.TICKET_MESSAGE_KINDS, { error: 'Tipo di messaggio non valido: «risposta» o «nota»' }),
    text: ticketMessageText(),
    /**
     * Facoltativo, solo con `risposta` (#159): l'`updatedAt` della bozza di risposta caricata nel
     * composer (`TicketDetail.replyDraft.updatedAt`). All'invio si cancella solo quella bozza, non una
     * più nuova arrivata via MCP mentre l'operatore la rivedeva. Senza, si cancella la bozza salvata
     * prima che l'invio partisse (#158). Non entra nell'impronta dell'idempotenza.
     */
    replyDraftUpdatedAt: zod_1.z.iso
        .datetime({ offset: true, error: "Bozza caricata: data e ora ISO 8601 dell'ultimo salvataggio" })
        .optional()
        .meta({
        description: "Solo con `risposta`: l'`updatedAt` della bozza di risposta caricata nel composer. All'invio si cancella solo quella bozza, non una più nuova.",
    }),
})
    .refine((value) => value.replyDraftUpdatedAt === undefined || value.kind === 'risposta', {
    error: 'La bozza di risposta si invia come risposta, non come nota interna',
    path: ['replyDraftUpdatedAt'],
});
/** Codici di errore della scrittura dal ticket, nel campo `code` del corpo di errore dell'API. */
exports.TICKET_MESSAGE_ERROR_CODES = {
    /** Risposta su un ticket `Risolto`, `Chiuso` o `Scartato`: la riapertura è di #6 e #42. */
    replyNotAllowed: 'tickets.risposta_non_ammessa',
    /**
     * 500: l'email è partita ma la risposta non è stata registrata nel thread (`details.emailId`).
     * Non va reinviata: la stessa chiave di idempotenza non la rispedisce comunque.
     */
    replyNotRecorded: 'tickets.risposta_non_registrata',
    /**
     * 409: con questa chiave di idempotenza c'è già una risposta in invio, oppure il suo esito non è
     * stato registrato. Non va reinviata: si ricarica il ticket.
     */
    replyInProgress: 'tickets.risposta_in_invio',
};
/**
 * Cambi di stato che una risposta applica, in ordine, dallo stato corrente (DEC-002: la risposta
 * porta il ticket «In attesa dell'utente»). Da `nuovo` passa prima per `in_lavorazione`, così
 * entrambe le transizioni sono eventi del thread; da `in_attesa_utente` resta lì.
 *
 * @returns le transizioni da applicare (vuoto = nessun cambio), `null` se la risposta non è
 * ammessa: su `risolto` la riapertura è un'azione a sé (#6, #42), `chiuso` e `scartato` sono terminali
 */
function replyStatePath(state) {
    switch (state) {
        case 'nuovo':
            return ['in_lavorazione', 'in_attesa_utente'];
        case 'in_lavorazione':
            return ['in_attesa_utente'];
        case 'in_attesa_utente':
            return [];
        case 'risolto':
        case 'chiuso':
        case 'scartato':
            return null;
    }
}
/** Vero se da questo stato si può rispondere a chi ha segnalato (la nota interna è sempre ammessa). */
function canReply(state) {
    return replyStatePath(state) !== null;
}
const URL_PATTERN = /https?:\/\/[^\s<>"]+/g;
/** Punteggiatura finale che chiude la frase, non l'URL («vedi https://x.it/a.»). */
const TRAILING_PUNCTUATION = /[.,;:!?)\]'»]+$/;
/**
 * Divide un testo semplice in testo e URL (DEC-008: URL cliccabili nel thread e nell'email HTML).
 * Solo `http://` e `https://`: nessun altro schema (`javascript:`, `data:`) diventa un link. La
 * punteggiatura che chiude la frase resta fuori dall'URL.
 */
function linkifyText(text) {
    const parts = [];
    let last = 0;
    for (const match of text.matchAll(URL_PATTERN)) {
        const url = match[0].replace(TRAILING_PUNCTUATION, '');
        // «https://» senza nulla dopo non è un indirizzo: resta testo.
        if (!/^https?:\/\/[^/?#]/.test(url))
            continue;
        if (match.index > last)
            parts.push({ type: 'text', value: text.slice(last, match.index) });
        parts.push({ type: 'link', url });
        last = match.index + url.length;
    }
    if (last < text.length)
        parts.push({ type: 'text', value: text.slice(last) });
    return parts;
}
//# sourceMappingURL=ticket-messages.js.map